import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { LoadMoreFooter } from '@/components/LoadMoreFooter';
import { PageHero } from '@/components/layout/PageHero';
import { useProductsLoadMore, useProductsStats } from '@/features/product/hooks';
import {
  useProductListControls,
  validateProductListSearch,
} from '@/features/product/list-controls';
import {
  ListEmptyState,
  ListErrorState,
  ListLoadingState,
} from '@/features/product/components/table-kit';
import { ClinicalProtocolFinderTable } from '@/features/clinical-protocol/components/ClinicalProtocolFinderTable';

/**
 * Clinical Protocols — list page (table view).
 *
 * Same panel + table layout as the predicate finder and regulatory strategy
 * list pages. Clicking Run dispatches a clinical-protocol run and navigates
 * to /clinical-protocols/$productId for polling + rendering.
 */
export const Route = createFileRoute('/clinical-protocols')({
  validateSearch: validateProductListSearch,
  component: ClinicalProtocolsPage,
});

function ClinicalProtocolsPage() {
  // Keyset-paginated list (10/page, PER-PAGE Load more) + server-accurate
  // tile counts — the tiles must not be computed from loaded rows only.
  // Search + sort are SERVER params (debounced q; sort key + direction) so
  // results cover the whole roster, not just the loaded rows.
  const searchParams = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const {
    searchInput,
    setSearchInput,
    q,
    sort,
    onSort,
    productType,
    onProductType,
    filtering,
  } = useProductListControls(searchParams, navigate);
  const { visible, hasMore, isFetchingMore, loadMore, isLoading, isError, totalCount } =
    useProductsLoadMore(10, { q, sort, productType, dispatchReady: true });
  const statsQuery = useProductsStats();
  const stats = {
    // dispatch_ready, not total: this table lists dispatch-ready products
    // only, and a badge that counts rows the table withholds is what made
    // the same "Products" number differ from tab to tab.
    total: statsQuery.data?.dispatch_ready ?? 0,
    analyzed: statsQuery.data?.analyzed['protocol'] ?? 0,
    running: statsQuery.data?.running['protocol'] ?? 0,
    marketUpdates: statsQuery.data?.market_updates['protocol'] ?? 0,
  };

  return (
    <>
      <PageHero
        title="Clinical Protocols"
        subtitle="Generate a regulator-aligned clinical protocol — inclusion/exclusion criteria, endpoints, statistical plan, and safety monitoring — grounded in trial-registry precedents and current regulatory guidance."
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-20">
        {isLoading && <ListLoadingState />}
        {isError && <ListErrorState />}
        {!isLoading && !isError && visible.length === 0 && !filtering && (
          <ListEmptyState
            title="No products to analyze yet."
            body="Register a product to generate a clinical protocol for it."
          >
            <Button variant="brand" asChild>
              <a href="/product-account">Create a product</a>
            </Button>
          </ListEmptyState>
        )}
        {!isLoading && !isError && (visible.length > 0 || filtering) && (
          <>
            <ClinicalProtocolFinderTable
              products={visible}
              stats={stats}
              search={searchInput}
              onSearch={setSearchInput}
              sort={sort}
              onSort={onSort}
              productType={productType}
              onProductType={onProductType}
            />
            <LoadMoreFooter
              loaded={visible.length}
              total={totalCount}
              hasMore={hasMore}
              isFetching={isFetchingMore}
              onLoadMore={loadMore}
            />
          </>
        )}
      </main>
    </>
  );
}


